import React, { useEffect } from 'react';

import Button from '@amzn/meridian/button';
import Loader from '@amzn/meridian/loader';
import Row from '@amzn/meridian/row';
import useForm from 'src/hooks/useForm';
import {
  createAccommodation,
  getEmailSettingsForSite,
  setApprovalFormData,
  setApprovalFormErrors,
  setIsCancelModalOpen,
  setPageProgress,
} from 'src/store/newJmrPageSlice/newJmrPageSlice';
import { type ApprovalFormData } from 'src/store/newJmrPageSlice/types';
import { useAppDispatch, useAppSelector } from 'src/store/store';
import { ApiCallStatus } from 'src/store/type';
import { isEmailValid } from 'src/utils/helpers';
import { JmrPageProgress } from 'src/utils/jmrUtils';

import { NewJMRPageFooter, NewJMRPageMainBody, NewJMRPageWrapper } from '../pageLayout/pageLayout';

import ApprovalEmailForm from './approvalEmailForm/approvalEmailForm';

/**
 * Default component
 */
const SendForApprovalPage = () => {
  const dispatch = useAppDispatch();
  const { approvalFormData, approvalFormErrors, accommodationSavingStatus } = useAppSelector(
    (state) => state.newJmrPage,
  );

  // Setup form hooks
  const { validateForm, setFormValues, errors } = useForm<ApprovalFormData>({
    validations: {
      emailRecipients: {
        custom: {
          isValid: (value) => isEmailValid(value),
          message: 'Must be valid email',
        },
      },
    },
  });

  const handleChange = (key: keyof ApprovalFormData, value: any) => {
    dispatch(setApprovalFormData({ ...approvalFormData, [key]: value }));
    dispatch(setApprovalFormErrors({ ...approvalFormErrors, [key]: undefined }));
  };

  useEffect(() => {
    if (approvalFormData) {
      setFormValues(approvalFormData);
    }
  }, [approvalFormData]);

  useEffect(() => {
    dispatch(setApprovalFormErrors(errors));
  }, [dispatch, errors]);

  useEffect(() => {
    dispatch(getEmailSettingsForSite());
  }, [dispatch]);

  return (
    <NewJMRPageWrapper>
      {/* Main Body */}
      <NewJMRPageMainBody title="Approval Notification" xs="85%" md="75%" lg="50%">
        <ApprovalEmailForm handleChange={handleChange} />
      </NewJMRPageMainBody>

      {/* Footer */}
      <NewJMRPageFooter>
        <Row spacing="400">
          <div></div>
          <Button type="link" onClick={() => dispatch(setIsCancelModalOpen(true))}>
            Cancel
          </Button>
          <Button type="tertiary" onClick={() => dispatch(setPageProgress(JmrPageProgress.Review))}>
            Previous
          </Button>
          <Button
            data-cy="new-jmr__approval__next"
            disabled={accommodationSavingStatus === ApiCallStatus.Pending}
            onClick={() => (validateForm() ? dispatch(createAccommodation()) : null)}
          >
            {accommodationSavingStatus === ApiCallStatus.Pending ? <Loader size="small" /> : 'Send for Approval'}
          </Button>
        </Row>
      </NewJMRPageFooter>
    </NewJMRPageWrapper>
  );
};

export default SendForApprovalPage;
